import { collection, doc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { isFirestoreEnabled } from '../config/featureFlags';
import Papa from 'papaparse';

// Cache for CSV data
let csvCache = null;

export const getDevotionalForDay = async (day) => {
  console.log('getDevotionalForDay called with day:', day);
  console.log('Firestore enabled:', isFirestoreEnabled());

  try {
    if (isFirestoreEnabled()) {
      return await getDevotionalFromFirestore(day);
    } else {
      return await getDevotionalFromCSV(day);
    }
  } catch (error) {
    console.error('Error in getDevotionalForDay:', error);
    throw error;
  }
};

const getDevotionalFromFirestore = async (day) => {
  try {
    console.log('Attempting to fetch from Firestore for day:', day);
    const devotionalsRef = collection(db, 'devotionals');

    // Ensure day is a number
    const dayNum = parseInt(day);
    if (isNaN(dayNum)) {
      throw new Error('Invalid day number');
    }

    console.log('Querying with day:', dayNum);
    const q = query(devotionalsRef, where('day', '==', dayNum));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log('No devotional found for day:', dayNum);
      return null;
    }

    const devotional = snapshot.docs[0].data();
    console.log('Found devotional:', devotional);

    // Map the data to match the expected structure
    return {
      id: snapshot.docs[0].id,
      chapter: devotional.chapter || 1,
      verse: devotional.verse || 1,
      scriptureReference: devotional.scriptureReference || '',
      verseText: devotional.verseText || devotional.scriptureReference || '',
      title: devotional.title || '',
      devotionalContent: devotional.devotionalContent || '',
      prayer: devotional.prayer || '',
      dailyChallenge: devotional.dailyChallenge || '',
      translation: devotional.translation || 'NET',
      day: devotional.day,
      lastModified: devotional.lastModified,
      author: devotional.author || 'system',
      originalDate: devotional.originalDate || ''
    };
  } catch (error) {
    console.error('Error in getDevotionalFromFirestore:', error);
    throw error;
  }
};

// Helper function to get content with fallback
const getContentWithFallback = (row, primaryKey, fallbackKey) => {
  const primary = row[primaryKey]?.trim() || '';
  const fallback = row[fallbackKey]?.trim() || '';
  return primary || fallback;
};

const getDevotionalFromCSV = async (day) => {
  try {
    console.log('Attempting to fetch from CSV for day:', day);
    if (!csvCache) {
      const response = await fetch('/data/ProverbsDailyDevotionalData.csv');
      if (!response.ok) {
        throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
      }
      const csvText = await response.text();
      const parsed = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim()
      });

      // Map CSV data to match our structure with fallbacks
      csvCache = parsed.data.map(row => ({
        day: parseInt(row['Day of Month']),
        chapter: parseInt(row['Chapter Number']) || 1,
        verse: parseInt(row['Verse Number']) || 1,
        scriptureReference: `Proverbs ${row['Chapter Number']}:${row['Verse Number']}`,
        verseText: row['NET_Translation'] || row['Scripture Reference'] || '',
        title: getContentWithFallback(row, 'Custom Devo Title Created', 'Devo Title'),
        devotionalContent: getContentWithFallback(row, 'Daily Devotionals', 'Daily Observation'),
        prayer: getContentWithFallback(row, 'Custom Prayer Created', 'Short Prayer'),
        dailyChallenge: getContentWithFallback(row, 'Custom Challenge Created', 'Daily Challenge'),
        lastModified: new Date().toISOString(),
        author: 'system'
      }));

      console.log('CSV data loaded into cache. First row:', csvCache[0]);
    }

    const result = csvCache.find(row => row.day === parseInt(day));
    console.log('CSV result for day', day, ':', result);
    return result;
  } catch (error) {
    console.error('Error in getDevotionalFromCSV:', error);
    throw error;
  }
};

export const updateDevotional = async (day, updates) => {
  console.log('updateDevotional called with:', { day, updates });

  if (!isFirestoreEnabled()) {
    throw new Error('Updates are only supported when Firestore is enabled');
  }

  try {
    const devotionalsRef = collection(db, 'devotionals');

    // Try with number first
    const dayNum = parseInt(day);
    console.log('Querying for update with number:', dayNum);
    let q = query(devotionalsRef, where('day', '==', dayNum));
    let snapshot = await getDocs(q);

    // If no results, try with string
    if (snapshot.empty) {
      console.log('No results with number, trying string for update');
      q = query(devotionalsRef, where('day', '==', day.toString()));
      snapshot = await getDocs(q);
    }

    if (snapshot.empty) {
      throw new Error(`No devotional found for day ${day}`);
    }

    const docRef = doc(devotionalsRef, snapshot.docs[0].id);
    const currentData = snapshot.docs[0].data();

    // Calculate the date for this day
    const currentYear = new Date().getFullYear();
    const date = new Date(currentYear, 0, 1);
    date.setDate(date.getDate() + dayNum - 1);
    date.setFullYear(2025); // Set to 2025 as that's what we're using

    const updateData = {
      ...currentData, // Keep existing data
      ...updates, // Apply updates
      day: dayNum, // Ensure day is stored as a number
      dateToDisplay: `${date.getMonth() + 1}/${date.getDate()}/2025`,
      date: date,
      originalDate: `${date.getMonth() + 1}/${date.getDate()}/2025`,
      lastModified: new Date().toISOString(),
      validationStatus: 'valid',
      verseText: updates.verseText || updates.scriptureReference // Only use scriptureReference as fallback
    };

    console.log('Updating document with data:', updateData);
    await updateDoc(docRef, updateData);
    console.log('Update successful');

    // Clear any local cache
    localStorage.removeItem('devotionalCache');

    return true;
  } catch (error) {
    console.error('Detailed error in updateDevotional:', {
      error,
      errorMessage: error.message,
      errorCode: error.code,
      errorStack: error.stack
    });
    throw error;
  }
}; 